import { useEditorStore } from '../../store/editor-store';
import { CartButton } from '../CartButton';
import { BrandLogo } from '../BrandLogo';
import { IconCart, IconEye, IconGallery, IconProduct, IconText, IconUpload } from '../Icons';

export function MobileHeader({
  onDetails,
  cartCount = 0,
  cartUrl,
  logoUrl,
  logoHref,
  showCart = true,
  showSaveTemplate = false,
  onSaveTemplate,
  priceLabel,
  onPriceClick,
}: {
  onDetails?: () => void;
  cartCount?: number;
  cartUrl?: string;
  logoUrl?: string | null;
  logoHref?: string | null;
  showCart?: boolean;
  showSaveTemplate?: boolean;
  onSaveTemplate?: () => void;
  /** Live price in the top bar (desktop-style). */
  priceLabel?: string;
  onPriceClick?: () => void;
}) {
  const model = useEditorStore((s) => s.model);
  if (!model) return null;

  const canOpenPrice = !!onPriceClick && !!priceLabel;

  return (
    <header className="ox-m-header">
      <BrandLogo url={logoUrl} href={logoHref} className="ox-m-logo" />
      <div className="ox-m-header-mid">
        <div className="ox-m-product-name">
          <span className="ox-m-product-title">{model.name}</span>
          <button type="button" className="ox-m-info" title="Detalles" onClick={onDetails}>
            i
          </button>
        </div>
        {model.rating && (
          <div className="ox-m-rating">
            {'★'.repeat(Math.round(model.rating.value))}{' '}
            <span>
              {model.rating.value} ({model.rating.count})
            </span>
          </div>
        )}
      </div>
      {priceLabel ? (
        <button
          type="button"
          className="ox-m-header-price"
          disabled={!canOpenPrice}
          onClick={() => canOpenPrice && onPriceClick?.()}
          aria-label="Desglose de precios"
        >
          <span>{priceLabel}</span>
          {canOpenPrice ? (
            <span className="ox-price-chevron ox-price-chevron--down" aria-hidden>
              ⌄
            </span>
          ) : null}
        </button>
      ) : null}
      <div className="ox-d-topbar-actions">
        {showSaveTemplate && (
          <button type="button" className="ox-save-tpl-btn" onClick={onSaveTemplate}>
            Plantilla
          </button>
        )}
        {showCart ? (
          <CartButton count={cartCount} href={cartUrl} className="ox-cart-btn--header" />
        ) : !showSaveTemplate && !priceLabel ? (
          <span className="ox-m-header-spacer" aria-hidden />
        ) : null}
      </div>
    </header>
  );
}

export function MobileCanvasOverlays({
  saving,
  onSave,
  onPreview,
  saveLabel = '💾 Guardar',
}: {
  saving: boolean;
  onSave: () => void;
  onPreview: () => void;
  saveLabel?: string;
}) {
  return (
    <>
      <button type="button" className="ox-m-fab ox-m-fab-save" onClick={onSave} disabled={saving}>
        {saving ? '…' : saveLabel}
      </button>
      <button type="button" className="ox-m-fab ox-m-fab-view" onClick={onPreview}>
        <IconEye size={16} /> Ver
      </button>
    </>
  );
}

/** Horizontal view thumbnails (front / back / sleeves…) — mobile. */
export function MobileViewSwitcher() {
  const model = useEditorStore((s) => s.model);
  const activeView = useEditorStore((s) => s.activeView);
  const colorKey = useEditorStore((s) => s.document.selection.colorKey);
  const setActiveView = useEditorStore((s) => s.setActiveView);
  const color = model?.colors.find((c) => c.key === colorKey) ?? model?.colors[0];
  const views = model?.views ?? [];

  if (views.length < 2) return null;

  return (
    <div className="ox-m-views" role="tablist" aria-label="Vistas del producto">
      <div className="ox-m-views-scroll">
        {views.map((v) => {
          const src = color?.mockups[v.id]?.url;
          const active = activeView === v.id;
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`ox-m-view-thumb${active ? ' active' : ''}`}
              onClick={() => setActiveView(v.id)}
            >
              {src ? <img src={src} alt="" /> : <span className="ox-m-view-ph" />}
              <span className="ox-m-view-label">{v.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MobileProductActions({
  onDetails,
  onChangeProduct,
}: {
  onDetails: () => void;
  onChangeProduct: () => void;
}) {
  return (
    <div className="ox-m-product-actions">
      <button type="button" onClick={onDetails}>
        <span className="ox-m-pa-ico">i</span>
        Detalles del producto
      </button>
      <button type="button" onClick={onChangeProduct}>
        <span className="ox-m-pa-ico">↻</span>
        Cambiar producto
      </button>
    </div>
  );
}

export function MobileTabBar({
  active,
  onText,
  onUpload,
  onGallery,
  onProducts,
  onBuy,
  showBuy = true,
}: {
  active: 'text' | 'upload' | 'gallery' | 'products' | null;
  onText: () => void;
  onUpload: () => void;
  onGallery: () => void;
  onProducts: () => void;
  onBuy: () => void;
  showBuy?: boolean;
}) {
  return (
    <nav className={`ox-m-tabbar${showBuy ? ' ox-m-tabbar--buy' : ' ox-m-tabbar--nobuy'}`}>
      <button type="button" className={active === 'text' ? 'active' : ''} onClick={onText}>
        <span className="ox-m-tab-ico ox-m-tab-ico-svg">
          <IconText size={22} />
        </span>
        Texto
      </button>
      <button type="button" className={active === 'upload' ? 'active' : ''} onClick={onUpload}>
        <span className="ox-m-tab-ico ox-m-tab-ico-svg">
          <IconUpload size={22} />
        </span>
        Cargar
      </button>
      <button type="button" className={active === 'gallery' ? 'active' : ''} onClick={onGallery}>
        <span className="ox-m-tab-ico ox-m-tab-ico-svg">
          <IconGallery size={22} />
        </span>
        Galería
      </button>
      <button type="button" className={active === 'products' ? 'active' : ''} onClick={onProducts}>
        <span className="ox-m-tab-ico ox-m-tab-ico-svg">
          <IconProduct size={22} />
        </span>
        Productos
      </button>
      {showBuy && (
        <button type="button" className="ox-m-tab-buy" onClick={onBuy}>
          <span className="ox-m-tab-ico ox-m-tab-ico-svg">
            <IconCart size={20} />
          </span>
          Comprar
        </button>
      )}
    </nav>
  );
}
